import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MessageSquare, Rss } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

const STATUS_TONE: Record<string, string> = {
  idle:    'bg-surface-muted text-ink-tertiary',
  working: 'bg-status-draft-bg text-status-draft-fg',
  blocked: 'bg-status-danger-bg text-status-danger-fg',
}

export function AgentsPage() {
  const { data: agents = [] } = useQuery({ queryKey: ['agents'], queryFn: api.agents })
  const { data: activity = [] } = useQuery({ queryKey: ['agents.activity'], queryFn: api.agentActivity })

  return (
    <div className="p-3 md:p-5 max-w-[1400px] mx-auto flex flex-col gap-3.5">
      <header>
        <h1 className="text-xl font-medium">Agents</h1>
        <p className="text-xs text-ink-secondary mt-0.5">
          The five agents that back the editorial workflow. Each has its own system prompt, topics, and RSS feed.
        </p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {agents.map((a) => {
          const recent = activity.filter((ev) => ev.agent_id === a.id).slice(0, 3)
          return (
            <article
              key={a.id}
              className="border border-border rounded-md bg-surface-card p-3.5 flex flex-col gap-2.5"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span
                    className="w-9 h-9 rounded-md shrink-0"
                    style={{ background: a.avatar_color }}
                  />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{a.name}</div>
                    <div className="text-[11px] text-ink-tertiary">{a.role}</div>
                  </div>
                </div>
                <span className={cn('text-[10px] px-1.5 py-0.5 rounded shrink-0', STATUS_TONE[a.status])}>
                  {a.status}
                </span>
              </div>

              {a.topics.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {a.topics.slice(0, 4).map((t) => (
                    <span
                      key={t}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-surface-muted text-ink-secondary"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {recent.length > 0 && (
                <div className="border-t border-border pt-2 text-[11px] text-ink-secondary flex flex-col gap-0.5">
                  {recent.map((ev) => (
                    <div key={ev.id} className="truncate">
                      · {ev.summary}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-1.5 mt-auto pt-1">
                <Link
                  to={`/agents/${a.id}/chat`}
                  className="flex-1 bg-brand-600 text-white rounded-md px-2.5 py-1.5 text-xs text-center hover:opacity-90 flex items-center justify-center gap-1"
                >
                  <MessageSquare size={12} /> Chat
                </Link>
                <a
                  href={`/api/rss/agents/${a.id}/feed.xml`}
                  target="_blank"
                  rel="noreferrer"
                  className="border border-border rounded-md px-2.5 py-1.5 text-xs hover:bg-surface-muted flex items-center gap-1"
                  title="Open RSS feed"
                >
                  <Rss size={12} />
                </a>
              </div>
            </article>
          )
        })}
      </div>
    </div>
  )
}
